import { computed, effect, inject, Service, signal, Signal, untracked, WritableSignal } from '@angular/core';
import { Region } from 'angular-api-gateway/v1/model/region';
import { PickupStore, UserAddress } from '@lentau/address/shared/interfaces';
import { MissionAddressService } from '@lentau/address/mission-address/application/mission-address.service';
import { CityStoreFacade } from '@lentau/city/store/city.facade';
import { GrowthBookService } from '@lentau/core/growth-book/shared/growth-book.service';
import { DeliveryModeFacade } from '@lentau/delivery-mode/store/delivery-mode.facade';
import { BrowserApiSupportService } from '@lentau/ui-kit/core/services/browser-api-support/browser-api-support.service';
import { WINDOW } from '@lentau/ui-kit/core/tokens/window';
import { CityNavigateHelperService } from '../city-navigate-helper.service';
import { DOM_NEW_SITE_HOSTNAME, WEB_DOM_NEW_SITE_CITIES_FEATURE_KEY } from './dom-new-site-redirect.constants';
import {
  buildDomNewSiteRedirectUrl,
  isDomNewSiteCityAllowed,
  resolveDomNewSiteRegionId,
} from './dom-new-site-redirect.helper';
import { DomNewSiteRedirectStrategy } from './dom-new-site-redirect.strategy';
import { parseDomNewSiteCities } from './parse-dom-new-site-cities';
import { resolveDomPickupStoreFromUserStores } from './resolve-dom-pickup-store-from-user-stores.helper';

@Service()
export class DomNewSiteRedirectDomStrategy implements DomNewSiteRedirectStrategy {
  private readonly growthBookService: GrowthBookService = inject(GrowthBookService);
  private readonly browserApi: BrowserApiSupportService = inject(BrowserApiSupportService);
  private readonly window: Window = inject(WINDOW);
  private readonly cityFacade: CityStoreFacade = inject(CityStoreFacade);
  private readonly cityNavigateHelper: CityNavigateHelperService = inject(CityNavigateHelperService);
  private readonly missionAddressService: MissionAddressService = inject(MissionAddressService);
  private readonly deliveryModeFacade: DeliveryModeFacade = inject(DeliveryModeFacade);
  private readonly featuresRefreshed: WritableSignal<boolean> = signal(false);
  private readonly redirectRegionId: Signal<number | null> = computed(() => this.evaluateRedirectRegionId());

  public init(): void {
    if (!this.browserApi.isBrowserAndNotSearchBot()) {
      return;
    }

    this.refreshFeatures();
    this.watchCityForRedirect();
  }

  private refreshFeatures(): void {
    const initPromise: Promise<unknown> | undefined = this.growthBookService.awaitInitGrowthBook();

    if (initPromise) {
      initPromise
        .then(() => {
          this.featuresRefreshed.set(true);
        })
        .catch(() => undefined);
    }

    effect(() => {
      if (this.growthBookService.isInitialized()) {
        untracked(() => this.featuresRefreshed.set(true));
      }
    });
  }

  private watchCityForRedirect(): void {
    effect(() => {
      const regionId: number | null = this.redirectRegionId();

      untracked(() => this.redirectToNewSite(regionId));
    });
  }

  private redirectToNewSite(regionId: number | null): void {
    if (regionId === null) {
      return;
    }

    this.window.location.replace(buildDomNewSiteRedirectUrl(regionId));
  }

  private evaluateRedirectRegionId(): number | null {
    const citySlug: string = this.readResolvedCitySlug();
    const storeKeys: string[] = this.readStoreKeys();
    this.growthBookService.getCachedFeatures();
    const featureCities: string[] = parseDomNewSiteCities(this.readCitiesFeature());

    if (!this.featuresRefreshed() || this.isAlreadyOnNewSite()) {
      return null;
    }

    if (!isDomNewSiteCityAllowed({ citySlug, storeKeys, featureCities })) {
      return null;
    }

    return resolveDomNewSiteRegionId({ citySlug, storeKeys });
  }

  private readStoreKeys(): string[] {
    const store: PickupStore | null = this.readPickupStore();
    if (!store) {
      return [];
    }

    const keys: string[] = [];
    if (store.alias != null && String(store.alias).trim()) {
      keys.push(String(store.alias).trim());
    }
    if (store.id != null) {
      keys.push(String(store.id));
    }

    return keys;
  }

  private readPickupStore(): PickupStore | null {
    if (!this.missionAddressService.isDelivery()) {
      const missionAddress: PickupStore | UserAddress = this.missionAddressService.missionAddress();
      if (missionAddress && 'alias' in missionAddress) {
        return missionAddress as PickupStore;
      }
    }

    return (
      this.missionAddressService.currentStore() ??
      resolveDomPickupStoreFromUserStores(this.deliveryModeFacade.storeId(), this.deliveryModeFacade.userStores())
    );
  }

  private readResolvedCitySlug(): string {
    // На старте адрес/самовывоз уже может быть в кеше, а region/list ещё нет —
    // не откатываемся на citySlug из стора (часто moscow), ждём резолв из mission address.
    if (this.missionAddressService.missionAddress()) {
      return this.readMissionAddressCitySlug();
    }

    // currentStore ещё null, но delivery/mode уже отдал userStores — резолвим slug по regionId ТК
    if (!this.missionAddressService.isDelivery()) {
      const store: PickupStore | null = this.readPickupStore();
      const slugFromStore: string = this.citySlugByRegionId(store?.regionId);
      if (slugFromStore) {
        return slugFromStore;
      }
    }

    const citySlug: string = this.cityFacade.citySlug();
    const city: Region = this.cityFacade.city();
    const isResolvedSlug: boolean = !!citySlug && (city?.slug === citySlug || this.cityNavigateHelper.urlHasCitySlug);

    return isResolvedSlug ? citySlug : '';
  }

  private citySlugByRegionId(regionId: number | null | undefined): string {
    if (regionId == null) {
      return '';
    }

    return this.cityFacade.cities()?.find((region: Region) => region.id === regionId)?.slug ?? '';
  }

  private readMissionAddressCitySlug(): string {
    return this.missionAddressService.isDelivery()
      ? this.readDeliveryCitySlugFromTownTitle()
      : this.readPickupCitySlugFromRegionList();
  }

  private readDeliveryCitySlugFromTownTitle(): string {
    const address: UserAddress = this.missionAddressService.missionAddress() as UserAddress;
    const townTitle: string = address?.TownTitle?.trim();

    if (!townTitle) {
      return '';
    }

    const city: Region | undefined = this.cityFacade.cities()?.find((region: Region) => region.name === townTitle);

    return city?.slug ?? '';
  }

  private readPickupCitySlugFromRegionList(): string {
    const missionAddress: PickupStore | UserAddress = this.missionAddressService.missionAddress();
    if (!missionAddress || !('regionId' in missionAddress)) {
      return '';
    }

    return this.citySlugByRegionId((missionAddress as PickupStore).regionId);
  }

  private readCitiesFeature(): unknown {
    return this.growthBookService.getFeatureValue(WEB_DOM_NEW_SITE_CITIES_FEATURE_KEY).value;
  }

  private isAlreadyOnNewSite(): boolean {
    return this.window.location.hostname === DOM_NEW_SITE_HOSTNAME;
  }
}
